import {
  auth,
  logoutFirebase,
  postLogout,
  useAuthState
} from '@accumulus/shared';
import Logo from '@/components/logo';
import ChangePasswordDialog from '@/components/change-password-dialog';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {useMemo, useState} from 'react';
import {
  Users,
  Plane,
  Bot,
  Menu,
  X,
  MessageCircleQuestion,
  Megaphone,
  Radio,
  UserCog,
  FileText,
  MapPin,
  ScrollText,
  ClipboardList,
  Terminal,
  Globe,
  Waypoints,
  FlaskConical,
  CalendarPlus,
  FileQuestion,
  CircleDollarSign,
  Gauge,
  ListChecks,
  ShieldCheck,
  Shuffle,
  MessagesSquare,
  UsersRound,
  Tag,
  Wrench,
  Radar,
  FileJson,
  PanelLeftClose,
  PanelLeftOpen
} from 'lucide-react';
import {cn} from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import UTCText from '@/components/utc-text';
import {clearEmployee, useEmployeeRole} from '@/stores/employee';
import {
  canDrawSample,
  canOperate,
  canSimulate,
  canValidate,
  canViewAgentVersions,
  canViewDashboard,
  isKbiOnlyRole,
  type TRole
} from '@/lib/kbi-roles';

interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
  /** Hide the item when this returns false for the signed-in role. */
  visible?: (role: TRole) => boolean;
}

const NAV_ITEMS: NavItem[] = [
  {label: 'Users', path: '/onboarding-admin', icon: Users},
  {label: 'Airlines', path: '/airlines', icon: Plane},
  {label: 'Ask ACE Agent Execution', path: '/agent-execution', icon: Bot},
  {label: 'TN Agent Execution', path: '/tn-agent-execution', icon: Waypoints},
  {label: 'TN Simulation', path: '/tn-simulations', icon: FlaskConical},
  {
    label: 'Usage Cost',
    path: '/kbi/usage-cost',
    icon: CircleDollarSign,
    visible: canViewDashboard
  },
  {
    label: 'Flight Simulator',
    path: '/flight-simulator',
    icon: Radar,
    visible: canSimulate
  },
  {label: 'Events', path: '/events', icon: CalendarPlus},
  {
    label: 'AIR Lab RCP Scenarios',
    path: '/airlab-rcp-scenarios',
    icon: FileJson
  },
  {label: 'Questionnaires', path: '/questionnaires', icon: FileQuestion},
  {
    label: 'Suggested Questions',
    path: '/suggested-questions',
    icon: MessageCircleQuestion
  },
  {
    label: 'Promotions',
    path: '/promotions',
    icon: Megaphone
  },
  {
    label: 'FIR Assignments',
    path: '/user-fir-assignments',
    icon: Radio
  },
  {
    label: 'Atlas FIRs',
    path: '/atlas-firs',
    icon: Globe
  },
  {
    label: 'Employees',
    path: '/employees',
    icon: UserCog
  },
  {
    label: 'Airline Files',
    path: '/airline-files',
    icon: FileText
  },
  {
    label: 'Airport Notes',
    path: '/airport-notes',
    icon: MapPin
  },
  {
    label: 'User App Logs',
    path: '/user-app-logs',
    icon: ScrollText
  },
  {
    label: 'User Surveys',
    path: '/user-surveys',
    icon: ClipboardList
  },
  {
    label: 'API Logs',
    path: '/api-logs',
    icon: Terminal
  }
];

const KBI_NAV_ITEMS: NavItem[] = [
  {
    label: 'Success Criteria Overview',
    path: '/kbi',
    icon: Gauge,
    visible: canViewDashboard
  },
  {
    label: 'Negotiations',
    path: '/kbi/negotiations',
    icon: ListChecks,
    visible: canViewDashboard
  },
  {
    label: 'Validator',
    path: '/kbi/validator',
    icon: ShieldCheck,
    visible: canValidate
  },
  {
    label: 'Validator Samples',
    path: '/kbi/validator-samples',
    icon: Shuffle,
    visible: canDrawSample
  },
  {
    label: 'Baseline Chats',
    path: '/kbi/baseline',
    icon: MessagesSquare,
    visible: canOperate
  },
  {
    label: 'Participants',
    path: '/kbi/participants',
    icon: UsersRound,
    visible: canOperate
  },
  {
    label: 'Agent Versions',
    path: '/kbi/agent-versions',
    icon: Tag,
    visible: canViewAgentVersions
  },
  {
    label: 'Operations',
    path: '/kbi/operations',
    icon: Wrench,
    visible: canOperate
  }
];

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

/** The flight simulator run page is map-first: the sidebar shrinks to icons there. */
const COLLAPSED_KEY = 'flight-simulator-sidebar-collapsed';

const isRunPage = (pathname: string) =>
  pathname.startsWith('/flight-simulator/runs/');

const readCollapsed = (): boolean => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) !== 'false';
  } catch {
    return true;
  }
};

const SidebarContent = ({
  onClose,
  collapsed = false,
  onToggleCollapsed
}: {
  onClose: () => void;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const role = useEmployeeRole();

  const kbiItems = KBI_NAV_ITEMS.filter((item) => item.visible?.(role) ?? true);
  // kbi_operator / kbi_validator only work in the KBI section, plus the main
  // items gated on a KBI permission (Usage Cost).
  const mainItems = NAV_ITEMS.filter((item) =>
    item.visible ? item.visible(role) : !isKbiOnlyRole(role)
  );

  const isActive = (path: string) => {
    if (path === '/kbi') {
      return (
        location.pathname === '/kbi' ||
        location.pathname.startsWith('/kbi/kpis/')
      );
    }
    if (path === '/onboarding-admin') {
      return (
        location.pathname === '/onboarding-admin' ||
        location.pathname === '/'
      );
    }
    // Whole segments only: /kbi/validator is not /kbi/validator-samples.
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  return (
    <div className="flex h-full flex-col">
      <div
        className={cn(
          'flex h-14 items-center justify-between',
          collapsed ? 'justify-center px-2' : 'px-5'
        )}
      >
        {!collapsed && (
          <div
            className="flex-none cursor-pointer hover:opacity-80"
            onClick={() => {
              navigate('/');
              onClose();
            }}
          >
            <Logo />
          </div>
        )}
        {onToggleCollapsed && (
          <button
            type="button"
            className="hidden text-muted-foreground hover:text-foreground lg:block"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-4 w-4" />
            ) : (
              <PanelLeftClose className="h-4 w-4" />
            )}
          </button>
        )}
        <button
          className="text-muted-foreground hover:text-foreground lg:hidden"
          onClick={onClose}
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {mainItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            // A real link, so the browser offers "open in new tab" and
            // ctrl/cmd-click works; a plain click still navigates in place.
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose}
              aria-current={active ? 'page' : undefined}
              title={collapsed ? item.label : undefined}
              className={cn(
                'flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
                collapsed && 'justify-center px-0',
                active
                  ? 'bg-mountain-meadow/10 text-mountain-meadow'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground'
              )}
            >
              <Icon className="h-4 w-4" />
              {!collapsed && item.label}
            </Link>
          );
        })}
        {kbiItems.length > 0 && (
          <>
            {collapsed ? (
              <div className="mx-3 my-3 border-t border-border" />
            ) : (
              <div className="px-3 pb-1 pt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                AIRLAB POC
              </div>
            )}
            {kbiItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                // A real link, so the browser offers "open in new tab" and
                // ctrl/cmd-click works; a plain click still navigates in place.
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  aria-current={active ? 'page' : undefined}
                  title={collapsed ? item.label : undefined}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
                    collapsed && 'justify-center px-0',
                    active
                      ? 'bg-mountain-meadow/10 text-mountain-meadow'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {!collapsed && item.label}
                </Link>
              );
            })}
          </>
        )}
      </nav>
    </div>
  );
};

const HeaderActions = () => {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const nameText = useMemo(() => {
    const name = user?.displayName;
    if (!name) return 'MA';
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('');
  }, [user]);

  const onLogout = async () => {
    try {
      await postLogout();
    } catch {
      // ignore
    }
    localStorage.clear();
    clearEmployee();
    await logoutFirebase();
    setTimeout(() => navigate('/login'), 300);
  };

  return (
    <div className="flex items-center gap-3">
      <UTCText />
      {user && (
        <Popover>
          <PopoverTrigger asChild>
            <div className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-secondary text-xs text-spanish-gray-1">
              {nameText}
            </div>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-40">
            <div
              className="cursor-pointer py-2 text-sm text-foreground hover:text-mountain-meadow"
              onClick={onLogout}
            >
              Logout
            </div>
            <div
              className="cursor-pointer py-2 text-sm text-foreground hover:text-mountain-meadow"
              onClick={() => setChangePasswordOpen(true)}
            >
              Change Password
            </div>
          </PopoverContent>
        </Popover>
      )}

      <ChangePasswordDialog
        open={changePasswordOpen}
        onOpenChange={setChangePasswordOpen}
      />
    </div>
  );
};

const Sidebar = ({open, onClose}: SidebarProps) => {
  const location = useLocation();
  const runPage = isRunPage(location.pathname);
  const [collapsedPref, setCollapsedPref] = useState(readCollapsed);
  const collapsed = runPage && collapsedPref;
  const toggleCollapsed = () =>
    setCollapsedPref((prev) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, String(!prev));
      } catch {
        // Per-viewer convenience only.
      }
      return !prev;
    });

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={cn(
          'sticky top-0 hidden h-screen flex-none self-start border-r border-border bg-primary-light lg:block',
          collapsed ? 'w-16' : 'w-64'
        )}
      >
        <SidebarContent
          onClose={onClose}
          collapsed={collapsed}
          onToggleCollapsed={runPage ? toggleCollapsed : undefined}
        />
      </aside>

      {/* Mobile sidebar overlay */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={onClose}
          />
          <aside className="absolute left-0 top-0 h-full w-64 border-r border-border bg-primary-light">
            <SidebarContent onClose={onClose} />
          </aside>
        </div>
      )}
    </>
  );
};

const Layout = ({children}: {children: React.ReactNode}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex min-h-screen w-full bg-primary-dark">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-14 items-center justify-between border-b border-border px-6">
          <button
            className="text-muted-foreground hover:text-foreground lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1" />
          <HeaderActions />
        </div>
        <div className="flex-1">{children}</div>
      </div>
    </div>
  );
};

export default Layout;
